import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Fuel, FuelUnit } from "@/types/fuels/fuels.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fuelService } from "@/services/fuels/fuelService";
import { useData } from "@/hooks/useData";

interface FuelFormProps {
  isOpen: boolean;
  onClose: () => void;
  fuelToEdit?: Fuel | null;
  onSuccess: () => void;
}

const ALL_UNITS: FuelUnit[] = ["GJ", "L"];

export const FuelForm = ({
  isOpen,
  onClose,
  fuelToEdit,
  onSuccess,
}: FuelFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Get information sources for the dropdowns
  const { data: infoSources = [] } = useData<InformationSource>({
    collectionName: "setup_infoSources",
  });

  // Form state
  const [name, setName] = useState("");
  const [unit, setUnit] = useState<FuelUnit | "">("");
  const [pci, setPci] = useState<number | undefined>(undefined);
  const [pciSourceId, setPciSourceId] = useState("");
  const [density, setDensity] = useState<number | undefined>(undefined);
  const [densitySourceId, setDensitySourceId] = useState("");
  const [carbonFactor, setCarbonFactor] = useState<number | undefined>(
    undefined
  );
  const [carbonSourceId, setCarbonSourceId] = useState("");
  const [hydricFactor, setHydricFactor] = useState<number | undefined>(
    undefined
  );
  const [hydricSourceId, setHydricSourceId] = useState("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [unitError, setUnitError] = useState("");
  const [pciError, setPciError] = useState("");
  const [pciSourceError, setPciSourceError] = useState("");
  const [densityError, setDensityError] = useState("");
  const [densitySourceError, setDensitySourceError] = useState("");
  const [carbonFactorError, setCarbonFactorError] = useState("");
  const [carbonSourceError, setCarbonSourceError] = useState("");
  const [hydricFactorError, setHydricFactorError] = useState("");
  const [hydricSourceError, setHydricSourceError] = useState("");

  // Initialize form when editing or clear it when creating
  useEffect(() => {
    if (fuelToEdit) {
      setName(fuelToEdit.name || "");
      setUnit(fuelToEdit.units[0] || "");

      // Set carbon factor based on unit
      if (fuelToEdit.units.includes("GJ")) {
        setCarbonFactor(fuelToEdit.carbon_factors.fe_comb_GJ);
        setHydricFactor(fuelToEdit.hydric_factors.fh_comb_GJ);
      } else if (fuelToEdit.units.includes("L")) {
        setCarbonFactor(fuelToEdit.carbon_factors.fe_comb_L);
        setHydricFactor(fuelToEdit.hydric_factors.fh_comb_L);
        setDensity(fuelToEdit.densities.dens_comb);
        setPci(fuelToEdit.densities.pci);
      }

      // Set source IDs from existing fuel
      setPciSourceId(fuelToEdit.sources?.pci_source_id || "");
      setDensitySourceId(fuelToEdit.sources?.density_source_id || "");
      setCarbonSourceId(fuelToEdit.sources?.carbon_source_id || "");
      setHydricSourceId(fuelToEdit.sources?.hydric_source_id || "");
    } else {
      // Reset form for new creation
      setName("");
      setUnit("");
      setPci(undefined);
      setPciSourceId("");
      setDensity(undefined);
      setDensitySourceId("");
      setCarbonFactor(undefined);
      setCarbonSourceId("");
      setHydricFactor(undefined);
      setHydricSourceId("");
    }
    // Reset validation errors when opening/closing the form
    resetErrors();
  }, [fuelToEdit, isOpen]);

  const resetErrors = () => {
    setNameError("");
    setUnitError("");
    setPciError("");
    setPciSourceError("");
    setDensityError("");
    setDensitySourceError("");
    setCarbonFactorError("");
    setCarbonSourceError("");
    setHydricFactorError("");
    setHydricSourceError("");
  };

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate unit
    if (!unit) {
      setUnitError(t("validation.required"));
      isValid = false;
    } else {
      setUnitError("");
    }

    // Validate L-specific fields
    if (unit === "L") {
      // Validate PCI
      if (pci === undefined) {
        setPciError(t("validation.required"));
        isValid = false;
      } else if (pci < 0) {
        setPciError(t("validation.positiveNumber"));
        isValid = false;
      } else {
        setPciError("");
      }

      // Validate PCI source
      if (!pciSourceId) {
        setPciSourceError(t("validation.required"));
        isValid = false;
      } else {
        setPciSourceError("");
      }

      // Validate density
      if (density === undefined) {
        setDensityError(t("validation.required"));
        isValid = false;
      } else if (density < 0) {
        setDensityError(t("validation.positiveNumber"));
        isValid = false;
      } else {
        setDensityError("");
      }

      // Validate density source
      if (!densitySourceId) {
        setDensitySourceError(t("validation.required"));
        isValid = false;
      } else {
        setDensitySourceError("");
      }
    }

    // Validate carbon factor
    if (carbonFactor === undefined) {
      setCarbonFactorError(t("validation.required"));
      isValid = false;
    } else if (carbonFactor < 0) {
      setCarbonFactorError(t("validation.positiveNumber"));
      isValid = false;
    } else {
      setCarbonFactorError("");
    }

    // Validate carbon source
    if (!carbonSourceId) {
      setCarbonSourceError(t("validation.required"));
      isValid = false;
    } else {
      setCarbonSourceError("");
    }

    // Validate hydric factor
    if (hydricFactor === undefined) {
      setHydricFactorError(t("validation.required"));
      isValid = false;
    } else if (hydricFactor < 0) {
      setHydricFactorError(t("validation.positiveNumber"));
      isValid = false;
    } else {
      setHydricFactorError("");
    }

    // Validate hydric source
    if (!hydricSourceId) {
      setHydricSourceError(t("validation.required"));
      isValid = false;
    } else {
      setHydricSourceError("");
    }

    return isValid;
  };

  const handlePciChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setPci(undefined);
      return;
    }
    
    if (value === "." || value === "-" || value === "-.") {
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setPci(numValue);
    }
  };

  const handleDensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setDensity(undefined);
      return;
    }
    
    if (value === "." || value === "-" || value === "-.") {
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setDensity(numValue);
    }
  };

  const handleCarbonFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setCarbonFactor(undefined);
      return;
    }
    
    if (value === "." || value === "-" || value === "-.") {
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setCarbonFactor(numValue);
    }
  };

  const handleHydricFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setHydricFactor(undefined);
      return;
    }
    
    if (value === "." || value === "-" || value === "-.") {
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setHydricFactor(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const fuelData = {
        name: name.trim(),
        units: [unit as FuelUnit],
        carbon_factors: {
          ...(unit === "GJ" && { fe_comb_GJ: carbonFactor }),
          ...(unit === "L" && { fe_comb_L: carbonFactor }),
        },
        hydric_factors: {
          ...(unit === "GJ" && { fh_comb_GJ: hydricFactor }),
          ...(unit === "L" && { fh_comb_L: hydricFactor }),
        },
        densities: {
          ...(unit === "L" && { dens_comb: density }),
          ...(unit === "L" && { pci: pci }),
        },
        sources: {
          ...(unit === "L" && { pci_source_id: pciSourceId }),
          ...(unit === "L" && { density_source_id: densitySourceId }),
          carbon_source_id: carbonSourceId,
          hydric_source_id: hydricSourceId,
        },
      };

      if (fuelToEdit) {
        // Update existing fuel
        await fuelService.edit(fuelToEdit.id, fuelData, user?.uid);
        toast({
          title: t("success"),
          description: t("fuels.updated"),
        });
      } else {
        // Create new fuel
        await fuelService.create(
          fuelData as Omit<Fuel, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("fuels.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving fuel:", error);

      // Handle specific API errors
      if (error.message === "FUEL_DUPLICATE") {
        toast({
          title: t("error"),
          description: t("fuels.errors.duplicateFuel"),
          variant: "destructive",
        });
      } else {
        // Generic error handling
        toast({
          title: t("error"),
          description: fuelToEdit
            ? t("fuels.errors.updateError")
            : t("fuels.errors.createError"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived information sources for the dropdowns
  const activeInfoSources = infoSources.filter((source) => !source.isArchived);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {fuelToEdit ? t("fuels.editTitle") : t("fuels.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">
                  {t("fuels.name")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    if (!name.trim()) {
                      setNameError(t("validation.required"));
                    } else {
                      setNameError("");
                    }
                  }}
                  placeholder={t("fuels.namePlaceholder")}
                  className={`placeholder:text-gray-400 placeholder:text-sm ${
                    nameError ? "border-red-300" : ""
                  }`}
                  disabled={isLoading}
                />
                {nameError && (
                  <p className="text-sm font-medium text-red-500">
                    {nameError}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="unit">
                  {t("fuels.unit")} <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={unit}
                  onValueChange={(value) => {
                    setUnit(value as FuelUnit);
                    setUnitError("");
                    // Clear L-specific fields when switching away from L
                    if (value !== "L") {
                      setPci(undefined);
                      setPciSourceId("");
                      setDensity(undefined);
                      setDensitySourceId("");
                    }
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger
                    id="unit"
                    className={`w-full placeholder:text-gray-400 ${
                      unitError ? "border-red-300" : ""
                    }`}
                  >
                    <SelectValue placeholder={t("fuels.unitPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_UNITS.map((unitOption) => (
                      <SelectItem key={unitOption} value={unitOption}>
                        {unitOption}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {unitError && (
                  <p className="text-sm font-medium text-red-500">
                    {unitError}
                  </p>
                )}
              </div>
            </div>

            {/* L-specific fields */}
            {unit === "L" && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-medium mb-3">
                  {t("fuels.unitSpecificFields", { unit })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="pci">
                      {t("fuels.pci")} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pci"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("fuels.pciPlaceholder")}
                      value={pci ?? ""}
                      onChange={handlePciChange}
                      onBlur={() => {
                        if (pci === undefined) {
                          setPciError(t("validation.required"));
                        } else if (pci < 0) {
                          setPciError(t("validation.positiveNumber"));
                        } else {
                          setPciError("");
                        }
                      }}
                      className={`placeholder:text-gray-400 placeholder:text-sm ${
                        pciError ? "border-red-300" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {pciError && (
                      <p className="text-sm font-medium text-red-500">
                        {pciError}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="pciSource">
                      {t("fuels.pciSource")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={pciSourceId}
                      onValueChange={(value) => {
                        setPciSourceId(value);
                        setPciSourceError("");
                      }}
                      disabled={isLoading || activeInfoSources.length === 0}
                    >
                      <SelectTrigger
                        id="pciSource"
                        className={`w-full placeholder:text-gray-400 ${
                          pciSourceError ? "border-red-300" : ""
                        }`}
                      >
                        <SelectValue
                          placeholder={t("fuels.pciSourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {pciSourceError && (
                      <p className="text-sm font-medium text-red-500">
                        {pciSourceError}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="density">
                      {t("fuels.density")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="density"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("fuels.densityPlaceholder")}
                      value={density ?? ""}
                      onChange={handleDensityChange}
                      onBlur={() => {
                        if (density === undefined) {
                          setDensityError(t("validation.required"));
                        } else if (density < 0) {
                          setDensityError(t("validation.positiveNumber"));
                        } else {
                          setDensityError("");
                        }
                      }}
                      className={`placeholder:text-gray-400 placeholder:text-sm ${
                        densityError ? "border-red-300" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {densityError && (
                      <p className="text-sm font-medium text-red-500">
                        {densityError}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="densitySource">
                      {t("fuels.densitySource")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={densitySourceId}
                      onValueChange={(value) => {
                        setDensitySourceId(value);
                        setDensitySourceError("");
                      }}
                      disabled={isLoading || activeInfoSources.length === 0}
                    >
                      <SelectTrigger
                        id="densitySource"
                        className={`w-full placeholder:text-gray-400 ${
                          densitySourceError ? "border-red-300" : ""
                        }`}
                      >
                        <SelectValue
                          placeholder={t("fuels.densitySourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {densitySourceError && (
                      <p className="text-sm font-medium text-red-500">
                        {densitySourceError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footprint sections in a grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Carbon footprint section */}
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3">
                  {t("common.carbonFootprint")}
                </h3>

                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="carbonFactor">
                      {t("fuels.fe_comb")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="carbonFactor"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("fuels.fe_combPlaceholder")}
                      value={carbonFactor ?? ""}
                      onChange={handleCarbonFactorChange}
                      onBlur={() => {
                        if (carbonFactor === undefined) {
                          setCarbonFactorError(t("validation.required"));
                        } else if (carbonFactor < 0) {
                          setCarbonFactorError(t("validation.positiveNumber"));
                        } else {
                          setCarbonFactorError("");
                        }
                      }}
                      className={`placeholder:text-gray-400 placeholder:text-sm ${
                        carbonFactorError ? "border-red-300" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {carbonFactorError && (
                      <p className="text-sm font-medium text-red-500">
                        {carbonFactorError}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="carbonSource">
                      {t("fuels.fe_combSource")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={carbonSourceId}
                      onValueChange={(value) => {
                        setCarbonSourceId(value);
                        setCarbonSourceError("");
                      }}
                      disabled={isLoading || activeInfoSources.length === 0}
                    >
                      <SelectTrigger
                        id="carbonSource"
                        className={`w-full placeholder:text-gray-400 ${
                          carbonSourceError ? "border-red-300" : ""
                        }`}
                      >
                        <SelectValue
                          placeholder={t("fuels.fe_combSourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {carbonSourceError && (
                      <p className="text-sm font-medium text-red-500">
                        {carbonSourceError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Hydric footprint section */}
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3">
                  {t("common.hydricFootprint")}
                </h3>

                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="hydricFactor">
                      {t("fuels.fh_comb")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="hydricFactor"
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t("fuels.fh_combPlaceholder")}
                      value={hydricFactor ?? ""}
                      onChange={handleHydricFactorChange}
                      onBlur={() => {
                        if (hydricFactor === undefined) {
                          setHydricFactorError(t("validation.required"));
                        } else if (hydricFactor < 0) {
                          setHydricFactorError(t("validation.positiveNumber"));
                        } else {
                          setHydricFactorError("");
                        }
                      }}
                      className={`placeholder:text-gray-400 placeholder:text-sm ${
                        hydricFactorError ? "border-red-300" : ""
                      }`}
                      disabled={isLoading}
                    />
                    {hydricFactorError && (
                      <p className="text-sm font-medium text-red-500">
                        {hydricFactorError}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hydricSource">
                      {t("fuels.fh_combSource")}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={hydricSourceId}
                      onValueChange={(value) => {
                        setHydricSourceId(value);
                        setHydricSourceError("");
                      }}
                      disabled={isLoading || activeInfoSources.length === 0}
                    >
                      <SelectTrigger
                        id="hydricSource"
                        className={`w-full placeholder:text-gray-400 ${
                          hydricSourceError ? "border-red-300" : ""
                        }`}
                      >
                        <SelectValue
                          placeholder={t("fuels.fh_combSourcePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {activeInfoSources.map((source) => (
                          <SelectItem key={source.id} value={source.id}>
                            {source.name} ({source.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hydricSourceError && (
                      <p className="text-sm font-medium text-red-500">
                        {hydricSourceError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isLoading ? t("saving") : fuelToEdit ? t("update") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
